import type { ComponentChildren } from 'preact'
import type { WeightTrend } from '~/calculations/weightTrends'
import { TrendSummary } from '~/components/feedback/TrendSummary'
import { NumericField, TextField } from '~/components/primitives/Field'
import { OptionalSection } from '~/components/primitives/OptionalSection'
import { CONTEXT_TAG_LABELS, CONTEXT_TAGS, type ContextTag } from '~/models/contextTags'
import {
  OPTIONAL_RECOVERY_ITEM_PROMPTS,
  OPTIONAL_RECOVERY_ITEMS,
  RECOVERY_ITEM_PROMPTS,
  RECOVERY_ITEMS,
  type RecoveryItem,
} from '~/models/recoveryCheck'
import type { TrainingSessionCounts } from '~/models/trainingWeek'
import type { WeeklyReviewStep } from '~/models/weeklyReview'
import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import { RatingScale } from './RatingScale'
import styles from './WeeklyReviewWizard.module.css'

const SCALE = DEFAULT_RULES_THRESHOLDS.recovery

/**
 * Every step used to open with a lede paragraph above its inputs.
 *
 * **They are explanations, and C7.4 phase B moved them behind the step's
 * information control** (D-104 §5). Nothing was deleted: each one is now the
 * body of that step's sheet, in `stepExplanation` below, and the wizard renders
 * the control beside the step title.
 *
 * **What did not move is anything required to answer correctly.** The rating
 * scales still label both of their ends in words, the numeric fields still carry
 * "Out of 7" and their units, and the notes step still says a tag is a possible
 * explanation rather than a proven cause (D-040) — that sentence sits with the
 * tags rather than in the sheet, because it is what stops a tag reading as a
 * diagnosis. D-104 §4's failure mode, named so it can be watched for: an app
 * where nothing is explained and everything is a tap away.
 */

/* ---- Trend --------------------------------------------------------------- */

export function TrendStep({ trend, unit }: { trend: WeightTrend; unit: 'kg' | 'lb' }) {
  return <TrendSummary trend={trend} unit={unit} />
}

/* ---- Recovery ------------------------------------------------------------ */

export type RecoveryDraft = Partial<Record<string, number>>

interface RecoveryStepProps {
  ratings: RecoveryDraft
  optional: RecoveryDraft
  onChange: (item: string, value: number) => void
  onChangeOptional: (item: string, value: number) => void
}

export function RecoveryStep({ ratings, optional, onChange, onChangeOptional }: RecoveryStepProps) {
  return (
    <>
      {RECOVERY_ITEMS.map((item: RecoveryItem) => (
        <RatingScale
          key={item}
          legend={RECOVERY_ITEM_PROMPTS[item].question}
          low={RECOVERY_ITEM_PROMPTS[item].low}
          high={RECOVERY_ITEM_PROMPTS[item].high}
          min={SCALE.scaleMin}
          max={SCALE.scaleMax}
          value={ratings[item]}
          onChange={(value) => onChange(item, value)}
        />
      ))}

      {/* Collapsed on first render (D-104 §5). Two more scales are two more
          decisions on a screen that already asked for four, and a blank optional
          answer is missing evidence rather than a poor week — so leaving them
          unopened costs the user nothing and costs the engine nothing it counts
          against them. */}
      <OptionalSection label="Two more scales, if you want to answer them">
        {OPTIONAL_RECOVERY_ITEMS.map((item) => (
          <RatingScale
            key={item}
            legend={OPTIONAL_RECOVERY_ITEM_PROMPTS[item].question}
            low={OPTIONAL_RECOVERY_ITEM_PROMPTS[item].low}
            high={OPTIONAL_RECOVERY_ITEM_PROMPTS[item].high}
            min={SCALE.scaleMin}
            max={SCALE.scaleMax}
            value={optional[item]}
            onChange={(value) => onChangeOptional(item, value)}
          />
        ))}
      </OptionalSection>
    </>
  )
}

/* ---- Adherence ----------------------------------------------------------- */

export interface AdherenceDraft {
  readonly nutritionDaysOnPlan: string
  readonly trainingSessionsPlanned: string
  readonly trainingSessionsCompleted: string
  readonly stepTargetDays: string
  readonly proteinTargetDays: string
}

interface AdherenceStepProps {
  draft: AdherenceDraft
  onChange: (field: keyof AdherenceDraft, value: string) => void
  /**
   * Whether the two nutrition counts are asked here.
   *
   * False when the weekly nutrition step is in this user's list, because that
   * step collects the same two fields with wording that fits an external
   * authority. **Asking twice is the duplicate logging D-096 rules out**, and two
   * inputs bound to one field is also the reliable way to produce a screen where
   * the value the user sees depends on which one they typed in last.
   */
  showNutrition?: boolean
  /**
   * The week's own session counts, when it was materialised (D-105 §3).
   *
   * **Present means the two training fields are not asked**, because the answer
   * already exists: the schedule and the sessions the user completed, moved or
   * skipped are the record. Asking anyway would be a second authority the app
   * would have to choose between every time the two disagreed — the exact shape
   * D-099 §D rejected.
   *
   * **Absent is the legacy week**, which keeps the hand-typed fields and their
   * existing interpretation. Nothing is migrated: a week that was described by a
   * quota is still described by one.
   */
  derivedTraining?: TrainingSessionCounts
}

export function AdherenceStep({
  draft,
  onChange,
  showNutrition = true,
  derivedTraining,
}: AdherenceStepProps) {
  return (
    <>
      {showNutrition ? (
        <NumericField
          label="Days on plan with food"
          hint="Out of 7."
          suffix="days"
          value={draft.nutritionDaysOnPlan}
          onInput={(value) => onChange('nutritionDaysOnPlan', value)}
        />
      ) : null}
      {derivedTraining === undefined ? (
        <>
          <NumericField
            label="Training sessions planned"
            value={draft.trainingSessionsPlanned}
            onInput={(value) => onChange('trainingSessionsPlanned', value)}
          />
          <NumericField
            label="Training sessions completed"
            value={draft.trainingSessionsCompleted}
            onInput={(value) => onChange('trainingSessionsCompleted', value)}
          />
        </>
      ) : (
        <TrainingSummary counts={derivedTraining} />
      )}
      <NumericField
        label="Days you hit your step target"
        hint="Optional. Out of 7."
        suffix="days"
        value={draft.stepTargetDays}
        onInput={(value) => onChange('stepTargetDays', value)}
      />
      {showNutrition ? (
        <NumericField
          label="Days you hit your protein target"
          hint="Optional. Out of 7."
          suffix="days"
          value={draft.proteinTargetDays}
          onInput={(value) => onChange('proteinTargetDays', value)}
        />
      ) : null}
    </>
  )
}

/**
 * The week's training, reported rather than asked for.
 *
 * **Two numbers with a word between them, never a ratio** — the rule
 * `weekChecklist.ts` already applies, for the reason D-105 §5 restates: a ratio
 * invites a percentage and a percentage is a score. There is no "2 of 3" here
 * and there must not be.
 *
 * It says where a correction is made, because a value the user cannot edit and
 * cannot account for is worse than one they typed themselves.
 */
function TrainingSummary({ counts }: { counts: TrainingSessionCounts }) {
  return (
    <div class={styles.derived}>
      <p class={styles.derivedLabel}>Training sessions</p>
      <p class={styles.derivedValue}>
        {counts.planned} planned, {counts.completed} completed
      </p>
      <p class={styles.derivedNote}>
        Counted from this week&rsquo;s own sessions. To change one, complete, move or skip it on
        Today.
      </p>
    </div>
  )
}

/* ---- Nutrition, for an external authority -------------------------------- */

interface NutritionStepProps {
  draft: AdherenceDraft
  onChange: (field: keyof AdherenceDraft, value: string) => void
  /** What the user calls the authority. Shown here; never put in a rule's text. */
  providerLabel: string | undefined
}

/**
 * The lightweight weekly summary (D-096).
 *
 * Shown only for an **external nutrition app** whose daily questions the user
 * left off. They are already logging every meal there, so Project Sola asks for
 * the two counts it actually reads and nothing more — no food, no totals, and no
 * second copy of a diary that exists elsewhere.
 *
 * **It collects the same two fields the adherence step otherwise would**, rather
 * than new ones. D-096 introduces no coaching threshold, and "sufficiently
 * complete logging" reuses `adherence.nutritionDaysOnPlanToJudge`; a parallel
 * pair of fields would have needed a parallel pair of thresholds nobody approved.
 *
 * **The check-in question D-096 mentions is deliberately not collected**, and the
 * omission is stated rather than absorbed. No rule reads whether a check-in
 * happened, and a field nothing reads is the converse of D-060 — the rules say
 * "complete your check-in" as guidance instead, which needs no stored answer. It
 * becomes collectable the moment a rule needs it.
 */
export function NutritionStep({ draft, onChange, providerLabel }: NutritionStepProps) {
  const authority = nutritionAuthority(providerLabel)

  return (
    <>
      <NumericField
        label={`Days ${authority} logged completely`}
        hint="Out of 7. A rough count is fine — this decides how much of the week Project Sola will draw a conclusion from."
        suffix="days"
        value={draft.nutritionDaysOnPlan}
        onInput={(value) => onChange('nutritionDaysOnPlan', value)}
      />
      <NumericField
        label="Days you hit your protein target"
        hint="Optional. Out of 7."
        suffix="days"
        value={draft.proteinTargetDays}
        onInput={(value) => onChange('proteinTargetDays', value)}
      />
    </>
  )
}

/* ---- Notes and context tags ---------------------------------------------- */

interface NotesStepProps {
  notes: string
  tags: readonly ContextTag[]
  onNotes: (value: string) => void
  onToggleTag: (tag: ContextTag) => void
}

export function NotesStep({ notes, tags, onNotes, onToggleTag }: NotesStepProps) {
  return (
    /* The whole step is optional and starts collapsed (D-104 §5). A week with
       nothing unusual in it is the common case, and this is the last step before
       Project Sola's read — a user who has nothing to add presses Continue
       without reading anything. Nothing here is required by any rule: tags make
       the engine more cautious and never decide an outcome. */
    <OptionalSection label="Notes and context tags">
      {/* "Possible explanations, never proven causes" is a content rule, not a
          nicety — a tag must not read as a diagnosis of the week (D-040) — so it
          stays beside the tags rather than moving into the step's sheet. */}
      <p class={styles.lede}>
        Possible explanations, not proven causes. Project Sola uses them to be careful about
        reacting, never to decide what happened.
      </p>

      <fieldset class={styles.scale}>
        <legend class={styles.scaleLegend}>What was different this week?</legend>
        <div class={styles.tags}>
          {CONTEXT_TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              class={`${styles.tag} ${tags.includes(tag) ? styles.tagSelected : ''}`}
              aria-pressed={tags.includes(tag)}
              onClick={() => onToggleTag(tag)}
            >
              {CONTEXT_TAG_LABELS[tag]}
            </button>
          ))}
        </div>
      </fieldset>

      <TextField
        label="Notes"
        hint="Optional. Kept on this device, and excluded from AI-review exports by default."
        multiline
        value={notes}
        onInput={onNotes}
      />
    </OptionalSection>
  )
}

/* ---- The explanations, one per step -------------------------------------- */

/** What the user calls their nutrition authority, or a neutral stand-in. */
function nutritionAuthority(providerLabel: string | undefined): string {
  return providerLabel === undefined || providerLabel === '' ? 'your app' : providerLabel
}

/**
 * The step's own explanation, shown in a sheet rather than above its inputs.
 *
 * **One place, keyed by step**, rather than an `explanation` prop threaded
 * through six components: the wizard renders the control in its header beside
 * the title, so the text has to be reachable from there. A step with nothing
 * worth explaining returns `undefined` and gets no control — an information
 * button that opens a restatement of the title teaches the user that the button
 * is not worth pressing, which is how the whole pattern stops working.
 */
export function stepExplanation(
  step: WeeklyReviewStep,
  options: { readonly nutritionProviderLabel?: string | undefined } = {},
):
  | { readonly topic: string; readonly title: string; readonly body: ComponentChildren }
  | undefined {
  const authority = nutritionAuthority(options.nutritionProviderLabel)

  if (step === 'trend')
    return {
      topic: 'the week being judged',
      title: 'The week being judged',
      body: (
        <p>
          This is the week Project Sola will judge. Nothing here needs an answer — check it looks
          right before you go on.
        </p>
      ),
    }

  if (step === 'measurements')
    return {
      topic: 'taking measurements',
      title: 'Taking measurements',
      body: (
        <>
          <p>
            Three readings per site, always. Project Sola saves the middle one and warns you when
            they disagree by more than it can explain — a single tape measurement is not repeatable
            enough to draw a trend from.
          </p>
          <p>
            Skipping a site entirely is fine. Recording one with fewer than three readings is not,
            which is why there is no way to do it.
          </p>
        </>
      ),
    }

  if (step === 'strength')
    return {
      topic: 'the strength check',
      title: 'The strength check',
      body: (
        <p>
          Your heaviest working set on the lifts you chose to watch. Project Sola compares each one
          against its own recent average, never against anybody else's — it is looking for strength
          falling away under a deficit, not judging the number.
        </p>
      ),
    }

  if (step === 'recovery')
    return {
      topic: 'the recovery scales',
      title: 'How the week felt',
      body: (
        <p>
          Five is always the better end of every scale, including the two that sound backwards — the
          question is how well hunger was controlled, and how calm you felt.
        </p>
      ),
    }

  if (step === 'nutrition')
    return {
      topic: 'the nutrition summary',
      title: 'Why only two numbers',
      body: (
        <p>
          You track the detail in {authority}. Project Sola only needs to know how much of the week
          it can judge — it does not want your food, and there is nowhere here to put it.
        </p>
      ),
    }

  if (step === 'adherence')
    return {
      topic: 'following the plan',
      title: 'How closely you followed the plan',
      body: (
        <p>
          How closely the plan was followed — not what you ate. Project Sola needs this to know
          whether the plan was actually tested; without it, it will not suggest eating less.
        </p>
      ),
    }

  if (step === 'notes')
    return {
      topic: 'context tags',
      title: 'Anything unusual',
      body: (
        <p>
          Anything that might help explain this week. Project Sola uses it to be careful about
          reacting to one unusual week, never to decide what happened.
        </p>
      ),
    }

  return undefined
}
